import { useState, useEffect } from 'react';

import Hamburger from 'hamburger-react';
import { AppBar, Box, IconButton, Toolbar, Typography, useTheme } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneIcon from '@mui/icons-material/Phone';
import { useRouter } from 'next/router';

import {
	AnalyticsContext,
	App,
	BannerMobileAlertMessagesCollection,
	Maybe,
	NavigationLink,
	Page,
} from '@/types/generated';
import ImageWrapper from '@/components/ImageWrapper';
import TAGLink from '@/components/TAGLink';
import TAGButton from '@/components/TAGButton';
import TAGSvgIcon from '@/components/TAGSvgIcon/TAGSvgIcon';
import { useAppContext, useFacilityContext } from '@/context';
import HeaderWithLocationNavMobile from '@/components/HeaderWithLocationNav/HeaderMobile/HeaderMobile';
import LanguageToggle from '@/components/LanguageToggle/LanguageToggle';
import { clickContext } from '@/utils';
import { MOBILE_HEADER_ID } from '@/constants';
import GoogleMap from '@/components/GoogleMap';
import Drawer from '@/components/Drawer';
import { IGoogleMap } from '@/components/GoogleMap/GoogleMap';
import Banner from '@/components/Banner/Banner';

import OfficeInformationBlock from '../HeaderOfficeLocation/HeaderOfficeDetails/OfficeInformationBlock';
import useDisappearingHeader from '../hooks/useDissappearingHeader';
import NavigationBar from '../NavigationBar';
import { ODPLinksBlock, LocationLinksBlock } from '../HeaderOfficeLocation/HeaderOfficeDetails/HeaderOfficeDetails';
import { popupSchedulingStyles, locationIconStyles } from '../HeaderSearch/HeaderSearch.styles';
import NavigationMenu from '../NavigationMenu/NavigationMenu';
import HeaderSimpleFindOfficeLink from '../HeaderOfficeLocation/HeaderSimpleFindOfficeLink/HeaderSimpleFindOfficeLink';

import {
	getWrapperStyles,
	logoStyles,
	iconStyles,
	scheduleButtonStyles,
	toolbarHeaderStyles,
	toolbarActionsStyles,
	burgerAndLogoWrapperStyles,
	navMenuOfficeDetailsStyles,
	menuIconStyles,
	menuLinkStyles,
	getNavMenuNavigationBarStyles,
	menuCTAStyles,
	scheduleApptLinkStyles,
	locationNameToolbarStyles,
} from './HeaderMobile.styles';
import MobileMenu from './MobileMenu/MobileMenu';

export interface IHeaderMobile {
	headerData: App['header'];
	logoUrl?: Maybe<string>;
	withOfficeDetails?: boolean;
	isFullScreenMenu?: boolean;
	withMenuCTA?: boolean;
	brand?: Maybe<string>;
	hidePrimaryActionButton: boolean;
	hideLocation: boolean;
	showHeaderNavMenu?: boolean;
	logoHref: string;
	locationNavigationLinks?: Array<NavigationLink | Page> | undefined;
	phoneNumber?: Maybe<NavigationLink>;
	withFixedHeader?: boolean;
	showMobileHeaderLocationBar?: boolean;
	showLanguageToggle?: boolean;
	hideHamburgerMenuToggleButton?: boolean;
	useHeaderLocationCards?: boolean | null;
	googleMapsForLocationCards?: IGoogleMap;
	topAlertBannerMessages?: BannerMobileAlertMessagesCollection;
	bottomAlertBannerMessages?: BannerMobileAlertMessagesCollection;
	showTopAlertBannerMessages: boolean;
	showBottomAlertBannerMessages: boolean;
	variant?: Maybe<string>;
	hideOdpLinks?: boolean;
}

const getHiddenPosForHeaderScroll = (showTopAlertBannerMessages: boolean, showBottomAlertBannerMessages: boolean) => {
	// need to add 88px if top and bottom alert banners are shown
	if (showTopAlertBannerMessages && showBottomAlertBannerMessages) {
		return '-204px';
	}
	// need to add 40px if top and bottom alert banners are shown
	if (showTopAlertBannerMessages || showBottomAlertBannerMessages) {
		return '-156px';
	}
	return '-116px';
};

export default function HeaderMobile({
	headerData,
	logoUrl,
	withOfficeDetails = false,
	isFullScreenMenu = true,
	withMenuCTA = true,
	hidePrimaryActionButton,
	hideLocation,
	showHeaderNavMenu = false,
	logoHref = '/',
	locationNavigationLinks,
	phoneNumber,
	withFixedHeader = false,
	showMobileHeaderLocationBar = true,
	showLanguageToggle,
	hideHamburgerMenuToggleButton = false,
	useHeaderLocationCards = false,
	googleMapsForLocationCards,
	topAlertBannerMessages,
	bottomAlertBannerMessages,
	showTopAlertBannerMessages = false,
	showBottomAlertBannerMessages = false,
	variant = 'default',
	hideOdpLinks = false,
}: IHeaderMobile) {
	const theme = useTheme();
	// maybe disable this somehow for brands that do not use facilities
	const { officeInfo, locationState } = useFacilityContext();
	const { config } = useAppContext();
	const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
	const [isLocMenuOpen, setIsLocMenuOpen] = useState(false);
	const [isLocCardsMenuOpen, setIsLocCardsMenuOpen] = useState(false);
	const headerElement = useDisappearingHeader({
		hiddenPos: getHiddenPosForHeaderScroll(showTopAlertBannerMessages, showBottomAlertBannerMessages),
		showingPos: '0',
		withFixedHeader,
	});
	const [menuAnchorEl, setMenuAnchorEl] = useState<
		(HTMLDivElement & { getBoundingClientRect: () => { height: number } }) | null
	>(null);
	const [isPopScheduleMenuOpen, setIsPopScheduleMenuOpen] = useState<boolean>(false);
	const router = useRouter();

	const useSimpleFindOfficeLink = config.featureFlags.useSimpleFindOfficeLink;

	const utilityNavigation = headerData?.topNavigationLinksCollection;

	const phoneNumberNavItem = headerData?.topNavigationLinksCollection?.items
		?.map((item) => {
			if (
				item?.__typename === 'NavigationLink' &&
				item?.externalUrl?.includes('tel:') &&
				item?.linkText !== undefined
			) {
				return item;
			}
			return undefined;
		})
		.find((linkText) => linkText !== undefined);

	const mobileQuizLink = headerData?.mobileQuizLink as NavigationLink;

	const determinePhoneNumber = (
		phoneNumberNavItem: NavigationLink | undefined,
		phoneNumber: Maybe<NavigationLink> | undefined
	): string => {
		if (phoneNumberNavItem && phoneNumberNavItem.linkText !== undefined) {
			return phoneNumberNavItem.linkText || '#';
		} else if (phoneNumber && phoneNumber.linkText !== undefined) {
			return phoneNumber.linkText || '#';
		}
		return '#';
	};
	const showHeaderWithLocationNav = config.featureFlags.showHeaderWithLocationNav;

	useEffect(() => {
		if (isNavMenuOpen) {
			document.documentElement.style.overflowY = 'hidden';
		} else {
			document.documentElement.style.overflowY = 'inherit';
		}
	}, [isNavMenuOpen]);

	useEffect(() => {
		const handleRouteChange = () => {
			setIsNavMenuOpen(false);
		};

		router.events.on('routeChangeComplete', handleRouteChange);

		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [router.events]);

	const handleLocationLinkClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
		event.preventDefault();
		event.stopPropagation();

		if (withOfficeDetails) {
			if (useHeaderLocationCards && googleMapsForLocationCards) {
				setIsLocCardsMenuOpen(true);
			} else {
				setIsLocMenuOpen(true);
			}
			setIsNavMenuOpen(false);
		} else if (headerData?.locationBarFindOfficeUrl) {
			globalThis.location.href = headerData.locationBarFindOfficeUrl;
		}
	};

	const SearchButton = () => {
		return (
			<IconButton
				color="inherit"
				href={headerData?.locationBarFindOfficeUrl || '#'}
				sx={locationIconStyles.svgSize}
				data-testid="button_search_icon_mobile"
				aria-label="Search"
			>
				<SearchIcon data-testid="icon_search_on" />
			</IconButton>
		);
	};

	//NOTE: WellNow has a style override for the Header CTA (Scheduling Button)
	const SchedulingButtonMobile = () => {
		if (!headerData?.usePopupScheduling) {
			return (
				<TAGButton
					size="S"
					variant="primaryAccent1"
					sx={scheduleButtonStyles}
					href={
						headerData?.schedulingButtonUrlMobile ||
						headerData?.schedulingButtonUrl ||
						'/schedule-an-appointment'
					}
					dataTestId="button_schedule_mobile"
					analyticsContext={clickContext({ brand: config.name, type: 'scheduleClick' })}
				>
					{headerData?.schedulingButtonTextMobile || headerData?.schedulingButtonText || 'Schedule'}
				</TAGButton>
			);
		}
		return (
			<TAGButton
				size="S"
				variant="primaryAccent1"
				sx={scheduleButtonStyles}
				onClick={() => setIsPopScheduleMenuOpen(true)}
				dataTestId="button_schedule_mobile_popup"
				analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
			>
				{headerData?.schedulingButtonTextMobile || headerData?.schedulingButtonText || 'Schedule'}
			</TAGButton>
		);
	};

	const SchedulingButtonMobileMenu = () => {
		if (!headerData?.usePopupScheduling) {
			return (
				<TAGButton
					size="M"
					fullWidth
					variant="primaryDefault"
					href={
						headerData?.schedulingButtonUrlMobile ||
						headerData?.schedulingButtonUrl ||
						'/schedule-an-appointment'
					}
					dataTestId="button_schedule_mobile"
					sx={{ backgroundColor: 'secondary.main', mb: '0.75rem' }}
					analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
				>
					<CalendarTodayIcon sx={menuIconStyles} />
					{headerData?.schedulingButtonText || 'Schedule appointment'}
				</TAGButton>
			);
		}
		return (
			<TAGButton
				size="M"
				fullWidth
				variant="primaryDefault"
				onClick={() => setIsPopScheduleMenuOpen(true)}
				dataTestId="button_schedule_mobile_popup"
				sx={{ backgroundColor: 'secondary.main', mb: '0.75rem' }}
				analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
			>
				<CalendarTodayIcon sx={menuIconStyles} />
				{headerData?.schedulingButtonText || 'Schedule appointment'}
			</TAGButton>
		);
	};

	const PopupSchedulingMenu = () => {
		const toggleMenu = () => {
			setIsPopScheduleMenuOpen(false);
			setIsNavMenuOpen(false);
		};
		return (
			<MobileMenu
				open={isPopScheduleMenuOpen}
				onClose={() => setIsPopScheduleMenuOpen(false)}
				isFullScreenMenu={isFullScreenMenu}
				anchorEl={menuAnchorEl}
			>
				<Box display="flex" flexDirection="column" sx={popupSchedulingStyles.containerStyles}>
					<Typography variant="header3" sx={popupSchedulingStyles.headerStyles}>
						{headerData?.schedulingButtonTextMobile}
					</Typography>
					{headerData?.schedulingNavigationLinksCollection?.items.map((navItem, index) =>
						navItem ? (
							<Box key={index} sx={popupSchedulingStyles.navigationItemStyles}>
								<Typography
									variant="bodyLargeSemiBold"
									color="text.secondary"
									sx={popupSchedulingStyles.groupLinkLinkTextStyles}
								>
									{navItem.groupLink?.linkText}
								</Typography>
								{navItem.linksCollection?.items.map((link, index) => (
									<TAGLink
										key={index}
										onClick={toggleMenu}
										href={
											link?.isInternal
												? `${link?.pageData?.path as string}`
												: `${link?.externalUrl as string}`
										}
										noLinkStyle
										analyticsContext={
											link?.analyticsContext ?? ({ clickPosition: 'header' } as AnalyticsContext)
										}
									>
										<Typography variant="bodyLargeSemiBold" color="text.interactive">
											{link?.linkText}
										</Typography>
									</TAGLink>
								))}
							</Box>
						) : null
					)}
				</Box>
			</MobileMenu>
		);
	};

	if (showHeaderWithLocationNav) {
		return <HeaderWithLocationNavMobile headerData={headerData} logoHref={logoHref} logoUrl={logoUrl} />;
	}

	const isMotto = (config.name || '').toLowerCase().includes('motto');

	return (
		<AppBar
			ref={headerElement}
			sx={getWrapperStyles(
				theme,
				showMobileHeaderLocationBar,
				showTopAlertBannerMessages,
				showBottomAlertBannerMessages,
				variant || 'default'
			)}
			data-testid="section_header_mobile"
			id={MOBILE_HEADER_ID}
		>
			{showTopAlertBannerMessages && (
				<Banner
					dataTestId="header_mobile_top_alert_banner"
					variant={'Alert Banner'}
					mobileAlertMessagesCollection={topAlertBannerMessages}
				/>
			)}
			<Toolbar
				disableGutters
				sx={toolbarHeaderStyles}
				ref={(el) => setMenuAnchorEl(el)}
				data-testid="section_header_actions"
			>
				<Box sx={toolbarActionsStyles} data-testid="location_box_header_toolbar_root">
					<Box sx={burgerAndLogoWrapperStyles}>
						{!hideHamburgerMenuToggleButton && (
							<IconButton sx={iconStyles} data-testid="button_hamburger" tabIndex={-1}>
								<Hamburger
									size={24}
									toggled={isNavMenuOpen || isLocMenuOpen}
									toggle={setIsNavMenuOpen}
									color={variant === 'teamTag' ? 'white' : theme.palette.text.interactive}
									label="open menu"
								/>
							</IconButton>
						)}
						<TAGLink href={logoHref} analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}>
							<ImageWrapper
								src={logoUrl}
								width={variant === 'teamTag' ? '200px' : '116px'} // todo - add prop to set width from contentful
								height="auto"
								sx={logoStyles}
								data-testid="img_logo_mobile"
								alt="brand logo"
							/>
						</TAGLink>
					</Box>
					{showLanguageToggle && <LanguageToggle isLargeDevice />}
					<Box display="flex" justifyContent="flex-end" alignItems="center">
						{hideLocation ? null : <SearchButton />}
						{hidePrimaryActionButton ? null : <SchedulingButtonMobile />}
						<PopupSchedulingMenu />
					</Box>
				</Box>
				{showMobileHeaderLocationBar && !hideLocation && !useSimpleFindOfficeLink ? (
					<Box sx={locationNameToolbarStyles(theme)} data-testid="location_box_header_toolbar">
						<TAGButton
							size="S"
							variant="tertiaryHC"
							href={
								useHeaderLocationCards && googleMapsForLocationCards
									? undefined
									: headerData?.locationBarFindOfficeUrl ?? '#'
							}
							onClick={
								useHeaderLocationCards && googleMapsForLocationCards
									? () => {
											setIsLocCardsMenuOpen(true);
									  }
									: undefined
							}
							startIcon={
								<TAGSvgIcon icon="LocationOn" size={25} dataTestId={`start_icon_location_button`} />
							}
							analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
						>
							{officeInfo?.name
								? officeInfo.name
								: headerData?.locationBarWithOutOfficeTitle || 'Find an office'}
						</TAGButton>
					</Box>
				) : null}
			</Toolbar>

			<MobileMenu
				testIdSuffix="nav_menu"
				anchorEl={menuAnchorEl}
				open={isNavMenuOpen}
				onClose={() => setIsNavMenuOpen(false)}
				isFullScreenMenu={isFullScreenMenu}
				isWithNavMenu={showHeaderNavMenu}
			>
				{((withOfficeDetails && !withMenuCTA) || showHeaderNavMenu) && (
					<Box sx={navMenuOfficeDetailsStyles}>
						{!hideLocation && (
							<>
								{useSimpleFindOfficeLink ? (
									<HeaderSimpleFindOfficeLink
										locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl}
										locationBarWithOutOfficeTitle={headerData?.locationBarWithOutOfficeTitle ?? ''}
									/>
								) : (
									<TAGLink
										onClick={handleLocationLinkClick}
										dataTestId="link_facility_name"
										analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
									>
										<LocationOnOutlinedIcon sx={menuIconStyles} />

										<Typography
											sx={menuLinkStyles}
											variant="bodyMediumBold"
											color="text.primary"
											data-testid="text_facility_name"
										>
											{officeInfo?.name
												? officeInfo.name
												: headerData?.locationBarWithOutOfficeTitle || 'Find an office'}
										</Typography>
									</TAGLink>
								)}
							</>
						)}

						{headerData?.profileLinkUrl && (
							<TAGLink
								href={headerData.profileLinkUrl}
								analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
							>
								<PersonOutlineIcon sx={menuIconStyles} />
								<Typography
									sx={menuLinkStyles}
									variant="bodyMediumBold"
									color="text.primary"
									data-testid="text_user_profile_mobile"
								>
									{headerData.myAccountLabel || 'My account'}
								</Typography>
							</TAGLink>
						)}

						{!hidePrimaryActionButton && (
							<TAGLink
								href={
									headerData?.schedulingButtonUrl && isMotto
										? `${headerData.schedulingButtonUrl}${
												headerData.schedulingButtonUrl.endsWith('/') ? '' : '/'
										  }mottoaligners`
										: headerData?.schedulingButtonUrl || '/schedule-an-appointment'
								}
								linkSx={scheduleApptLinkStyles}
								analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
							>
								<CalendarTodayIcon sx={menuIconStyles} />
								<Typography
									sx={menuLinkStyles}
									variant="bodyMediumBold"
									color="text.primary"
									data-testid="text_schedule_link_mobile"
								>
									{headerData?.schedulingButtonText || 'Schedule appointment'}
								</Typography>
							</TAGLink>
						)}

						{phoneNumberNavItem || phoneNumber ? (
							<TAGLink
								noLinkStyle
								href={`tel:${determinePhoneNumber(phoneNumberNavItem, phoneNumber)}`}
								linkSx={scheduleApptLinkStyles}
								analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
							>
								<PhoneIcon sx={menuIconStyles} />
								<Typography
									sx={menuLinkStyles}
									variant="bodyMediumBold"
									color="text.primary"
									data-testid="text_phone_link_mobile"
								>
									{determinePhoneNumber(phoneNumberNavItem, phoneNumber)}
								</Typography>
							</TAGLink>
						) : null}

						{mobileQuizLink && (
							<>
								<TAGLink
									noLinkStyle
									href={mobileQuizLink.pageData?.path ?? '/'}
									linkSx={scheduleApptLinkStyles}
									analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
								>
									{mobileQuizLink.linkIcon?.url && (
										<Box
											component="img"
											src={mobileQuizLink.linkIcon.url}
											alt={mobileQuizLink.linkText || 'Link icon'}
											sx={menuIconStyles}
										/>
									)}
									<Typography
										sx={menuLinkStyles}
										variant="bodyMediumBold"
										color="text.primary"
										data-testid="text_quiz_link_mobile"
									>
										{mobileQuizLink.linkText}
									</Typography>
								</TAGLink>
							</>
						)}
					</Box>
				)}

				{showHeaderNavMenu ? (
					<NavigationMenu
						onClose={() => setIsNavMenuOpen(false)}
						isSmallScreen
						mainNavItems={headerData?.navigationMenu?.headerMainNavLinksCollection}
					/>
				) : (
					<NavigationBar
						onClose={() => setIsNavMenuOpen(false)}
						isSmallScreen
						navigationItems={headerData?.navigationLinksCollection}
						navigationSx={getNavMenuNavigationBarStyles(withOfficeDetails)}
					/>
				)}
				{!!utilityNavigation?.items?.length && !showHeaderNavMenu && (
					<NavigationBar
						testIdSuffix="utility"
						onClose={() => setIsNavMenuOpen(false)}
						isSmallScreen
						navigationItems={utilityNavigation}
						navigationSx={getNavMenuNavigationBarStyles(withOfficeDetails)}
						isUtilityNavigation
					/>
				)}
				{withMenuCTA && !showHeaderNavMenu && (
					<Box sx={menuCTAStyles}>
						{!hidePrimaryActionButton && <SchedulingButtonMobileMenu />}
						{!hideLocation && (
							<TAGButton
								size="M"
								fullWidth
								variant="secondaryDefault"
								onClick={handleLocationLinkClick}
								dataTestId="button_find_another_office_mobile"
								analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
							>
								<LocationOnOutlinedIcon sx={menuIconStyles} />
								{officeInfo?.name
									? officeInfo.name
									: headerData?.locationBarWithOutOfficeTitle || 'Find an office'}
							</TAGButton>
						)}
					</Box>
				)}
			</MobileMenu>

			<MobileMenu
				testIdSuffix="location_menu"
				anchorEl={menuAnchorEl}
				open={isLocMenuOpen}
				onClose={() => setIsLocMenuOpen(false)}
				isFullScreenMenu={isFullScreenMenu}
				isWithNavMenu={showHeaderNavMenu}
				transitionDuration={isNavMenuOpen ? 0 : 'auto'}
			>
				{officeInfo && (
					<Box display="flex" flexDirection="column">
						<Box padding="1.375rem 2.75rem 2rem 1.25rem">
							<OfficeInformationBlock office={officeInfo} distance={locationState?.distance} invert />
						</Box>
						{officeInfo && locationNavigationLinks ? (
							<LocationLinksBlock
								isSmallScreen
								locationNavigationLinks={locationNavigationLinks}
								officeInfo={officeInfo}
							/>
						) : officeInfo && !hideOdpLinks ? (
							<ODPLinksBlock isSmallScreen officeInfo={officeInfo} />
						) : null}
					</Box>
				)}
				{!hideLocation && (
					<Box padding="1.75rem 1.3rem" borderBottom="1px solid" borderColor="neutrals.primary">
						<TAGButton
							size="M"
							fullWidth
							variant="secondaryDefault"
							href={headerData?.locationBarFindOfficeUrl || '#'}
							dataTestId="button_find_another_office_mobile"
							analyticsContext={{ clickPosition: 'header' } as AnalyticsContext}
						>
							{headerData?.locationBarWithOfficeTitle || 'Find Another Office'}
						</TAGButton>
					</Box>
				)}
			</MobileMenu>

			<Drawer
				data-testid="mobile_location_cards_drawer"
				sheetVariant="elevation"
				closeButton
				open={isLocCardsMenuOpen}
				onClose={() => setIsLocCardsMenuOpen(false)}
				anchor="top"
				fullScreen
			>
				{googleMapsForLocationCards && (
					<GoogleMap isLocationCardsInHeader={true} {...googleMapsForLocationCards} />
				)}
			</Drawer>
			{showBottomAlertBannerMessages && (
				<Banner
					dataTestId="header_mobile_bottom_alert_banner"
					variant={'Alert Banner'}
					mobileAlertMessagesCollection={bottomAlertBannerMessages}
				/>
			)}
		</AppBar>
	);
}
